import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Container,
  Dimmer,
  Loader,
  Modal,
  Pagination,
  Segment,
  Table,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { getRecurringPaymentTransactionsAction } from 'state/business/actions';

const PAGE_SIZE = 10;

const mapStateToProps = ({ business }) => ({
  recurringPaymentTransactionsData: business.recurringPaymentTransactionsData,
  isFetching: business.gettingBusinessDetails,
  allBusiness: business.allBusiness,
});

const getStatusColor = status => {
  switch (status) {
    case 'SUCCESS':
      return 'green';
    case 'FAILED':
      return 'red';
    default:
      return 'black';
  }
};

const convertDate = payloadDate => {
  if (payloadDate === null) return '-';
  const newPayloadDate = payloadDate.substr(0, payloadDate.indexOf('Z') + 1);
  const dateTimeFormat = new Intl.DateTimeFormat('id', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
  const date = new Date(newPayloadDate);
  return dateTimeFormat.format(date);
};

const RecurringPaymentTransactions = connect(mapStateToProps)(props => {
  const {
    dispatch,
    allBusiness,
    recurringPaymentTransactionsData,
    isFetching,
  } = props;
  const businessId = allBusiness.data[allBusiness.activeIndex].id;

  const [errorData, setErrorData] = useState('');

  const showErrorModal = !!errorData;

  let paging = null;
  if (recurringPaymentTransactionsData) {
    paging = recurringPaymentTransactionsData.paging;
  }
  let totalPages = 0;
  if (paging) totalPages = Math.ceil(paging.total / paging.size);

  const fetchFirstPage = useCallback(() => {
    const payload = {
      businessId,
      page: 1,
      size: PAGE_SIZE,
    };

    dispatch(getRecurringPaymentTransactionsAction(payload));
  }, [businessId, dispatch]);

  const handleOnPageChange = (_, val) => {
    const { activePage } = val;
    const payload = {
      businessId,
      page: activePage,
      size: PAGE_SIZE,
    };
    dispatch(getRecurringPaymentTransactionsAction(payload));
  };

  useEffect(() => {
    fetchFirstPage();
  }, [businessId, fetchFirstPage]);

  return (
    <Container
      style={{ overflowY: 'scroll', maxHeight: '50vh', padding: '1vh' }}
    >
      <Segment style={{ padding: '0px' }}>
        <Dimmer active={isFetching}>
          <Loader size="massive">Loading</Loader>
        </Dimmer>
        <Table celled selectable style={{ margin: '0px' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>RPI Code</Table.HeaderCell>
              <Table.HeaderCell>Transaction ID</Table.HeaderCell>
              <Table.HeaderCell>Retry Attempt</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {recurringPaymentTransactionsData.data &&
            recurringPaymentTransactionsData.data.length > 0 ? (
              recurringPaymentTransactionsData.data.map(trx => {
                return (
                  <Table.Row key={trx.id}>
                    <Table.Cell>{convertDate(trx.created_at)}</Table.Cell>
                    <Table.Cell>{trx.rpi_code}</Table.Cell>
                    <Table.Cell
                      style={{ width: '400px', wordBreak: 'break-all' }}
                    >
                      {trx.external_transaction_id}
                    </Table.Cell>
                    <Table.Cell>{trx.external_retry_attempt}</Table.Cell>
                    <Table.Cell style={{ color: getStatusColor(trx.status) }}>
                      {trx.status}{' '}
                      {!!trx.external_errors && (
                        <Button
                          style={{ marginLeft: '8px' }}
                          size="mini"
                          onClick={() => setErrorData(trx.external_errors)}
                        >
                          Show Error
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <>
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    No Recurring Payment Transactions
                  </Table.Cell>
                </Table.Row>
              </>
            )}
          </Table.Body>

          {paging && paging.total > 0 ? (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="7">
                  <Pagination
                    activePage={paging.page}
                    totalPages={totalPages}
                    onPageChange={handleOnPageChange}
                    size="mini"
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          ) : (
            <></>
          )}
        </Table>
      </Segment>

      <Modal size="small" open={showErrorModal}>
        <Modal.Content>
          <p>{errorData}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setErrorData('')}>Close</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
});

RecurringPaymentTransactions.propTypes = {
  dispatch: PropTypes.func.isRequired,
  recurringPaymentTransactionsData: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  allBusiness: PropTypes.object.isRequired,
};

export default RecurringPaymentTransactions;
